import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from '../lib/api';
import { formatDateTime, formatRelative } from '../lib/format';
import {
  activityTypes as activityTypesApi,
  createActivity,
  deleteActivity,
  listLeads,
  updateActivity,
} from '../lib/resources';
import type { Activity } from '../types';
import { Button, ErrorNote, inputClass } from './ui';

/** Converte o valor de um <input type="datetime-local"> em ISO 8601. */
const toIso = (localValue: string): string => new Date(localValue).toISOString();

/** Agora + 1h, no formato que o datetime-local aceita. */
const defaultStart = (): string => {
  const date = new Date(Date.now() + 3600_000);
  date.setSeconds(0, 0);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

/**
 * Formulário de nova atividade.
 * Com `leadId`/`businessId` no contexto (card do negócio), o alvo já vem definido;
 * na tela de Atividades, o usuário escolhe o lead pela busca.
 */
export function ActivityComposer({
  leadId,
  businessId,
  onCreated,
  compact = false,
}: {
  leadId?: string;
  businessId?: string;
  onCreated?: () => void;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const hasContext = Boolean(leadId || businessId);

  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState(defaultStart);
  const [activityTypeId, setActivityTypeId] = useState('');
  const [description, setDescription] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [pickedLeadId, setPickedLeadId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const typesQuery = useQuery({
    queryKey: ['activity-types'],
    queryFn: () => activityTypesApi.list(),
  });

  const leadsQuery = useQuery({
    queryKey: ['leads', 'activity-picker', leadSearch],
    queryFn: () => listLeads({ search: leadSearch || undefined, take: 8 }),
    enabled: !hasContext,
  });

  const create = useMutation({
    mutationFn: () =>
      createActivity({
        title,
        startDate: toIso(startDate),
        description: description || undefined,
        activityTypeId: activityTypeId || undefined,
        businessId,
        leadId: businessId ? undefined : (leadId ?? pickedLeadId),
      }),
    onSuccess: () => {
      setTitle('');
      setDescription('');
      setStartDate(defaultStart());
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['lead-activities'] });
      queryClient.invalidateQueries({ queryKey: ['business'] });
      queryClient.invalidateQueries({ queryKey: ['stages'] });
      onCreated?.();
    },
    onError: (mutationError) =>
      setError(errorMessage(mutationError, 'Não foi possível criar a atividade')),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!hasContext && !pickedLeadId) {
      setError('Escolha o lead da atividade');
      return;
    }
    setError(null);
    create.mutate();
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 text-sm">
          <span className="text-xs text-slate-500">Título</span>
          <input
            required
            className={inputClass}
            placeholder="Ligar para confirmar a proposta"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <label className="text-sm">
          <span className="text-xs text-slate-500">Quando</span>
          <input
            required
            type="datetime-local"
            className={`${inputClass} w-52`}
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </label>

        <label className="text-sm">
          <span className="text-xs text-slate-500">Tipo</span>
          <select
            className={`${inputClass} w-40`}
            value={activityTypeId}
            onChange={(event) => setActivityTypeId(event.target.value)}
          >
            <option value="">Sem tipo</option>
            {(typesQuery.data?.data ?? []).map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </label>

        <Button type="submit" variant="primary" disabled={create.isPending}>
          {create.isPending ? 'Criando…' : 'Agendar'}
        </Button>
      </div>

      {!compact && (
        <label className="block text-sm">
          <span className="text-xs text-slate-500">Descrição</span>
          <input
            className={inputClass}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
      )}

      {!hasContext && (
        <div>
          <label className="block text-sm">
            <span className="text-xs text-slate-500">Lead</span>
            <input
              className={inputClass}
              placeholder="Buscar lead por nome, e-mail ou telefone"
              value={leadSearch}
              onChange={(event) => setLeadSearch(event.target.value)}
            />
          </label>
          <div className="mt-1 flex flex-wrap gap-1">
            {(leadsQuery.data?.data ?? []).map((lead) => (
              <button
                key={lead.id}
                type="button"
                onClick={() => setPickedLeadId(lead.id)}
                className={`rounded-full border px-2 py-0.5 text-xs ${
                  pickedLeadId === lead.id
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-300 text-slate-600 hover:border-slate-400'
                }`}
              >
                {lead.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}
    </form>
  );
}

/** Uma linha de atividade: concluir, ver quando, abrir o negócio e excluir. */
export function ActivityRow({
  activity,
  showBusinessLink = true,
  onChanged,
}: {
  activity: Activity;
  showBusinessLink?: boolean;
  onChanged?: () => void;
}) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['activities'] });
    queryClient.invalidateQueries({ queryKey: ['lead-activities'] });
    queryClient.invalidateQueries({ queryKey: ['business'] });
    queryClient.invalidateQueries({ queryKey: ['lead-history'] });
    queryClient.invalidateQueries({ queryKey: ['stages'] });
    onChanged?.();
  };

  const toggle = useMutation({
    mutationFn: () => updateActivity(activity.id, { isCompleted: !activity.isCompleted }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: () => deleteActivity(activity.id),
    onSuccess: invalidate,
  });

  const isLate = !activity.isCompleted && new Date(activity.startDate) < new Date();

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <input
        type="checkbox"
        checked={activity.isCompleted}
        onChange={() => toggle.mutate()}
        disabled={toggle.isPending}
        className="mt-1 h-4 w-4 shrink-0"
        aria-label={activity.isCompleted ? 'Reabrir atividade' : 'Concluir atividade'}
      />

      <div className="min-w-0 flex-1">
        <p
          className={`text-sm ${
            activity.isCompleted ? 'text-slate-400 line-through' : 'text-slate-800'
          }`}
        >
          {activity.title}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
          <span className={isLate ? 'font-medium text-red-600' : ''}>
            {formatDateTime(activity.startDate)}
            {isLate ? ' · atrasada' : ''}
          </span>
          {activity.activityType && (
            <span
              className="rounded-full px-2 py-0.5"
              style={{
                backgroundColor: `${activity.activityType.color ?? '#64748b'}1a`,
                color: activity.activityType.color ?? '#475569',
              }}
            >
              {activity.activityType.name}
            </span>
          )}
          {activity.isCompleted && activity.completedAt && (
            <span>concluída {formatRelative(activity.completedAt)}</span>
          )}
          {showBusinessLink && activity.businessId && (
            <Link to={`/negocios/${activity.businessId}`} className="hover:underline">
              ver negócio
            </Link>
          )}
          {showBusinessLink && !activity.businessId && activity.leadId && (
            <Link to={`/leads/${activity.leadId}`} className="hover:underline">
              ver lead
            </Link>
          )}
        </div>
        {activity.description && (
          <p className="mt-1 text-xs text-slate-500">{activity.description}</p>
        )}
      </div>

      <button
        type="button"
        onClick={() => remove.mutate()}
        className="text-xs text-slate-400 hover:text-red-600"
      >
        excluir
      </button>
    </div>
  );
}
